const EMAIL_SOUND_ENABLED_KEY = 'email_sound_enabled_v1'
const EMAIL_SOUND_ID_KEY = 'email_sound_id_v1'

export type EmailSoundId =
  | 'chime_1'
  | 'chime_2'
  | 'chime_3'
  | 'chime_4'
  | 'chime_5'
  | 'chime_6'
  | 'chime_7'
  | 'chime_8'
  | 'chime_9'
  | 'chime_10'

export const EMAIL_SOUND_OPTIONS: Array<{ id: EmailSoundId; label: string }> = [
  { id: 'chime_1', label: 'Chime 1 (Classic)' },
  { id: 'chime_2', label: 'Chime 2 (Soft)' },
  { id: 'chime_3', label: 'Chime 3 (Bright)' },
  { id: 'chime_4', label: 'Chime 4 (Double ding)' },
  { id: 'chime_5', label: 'Chime 5 (Ascending)' },
  { id: 'chime_6', label: 'Chime 6 (Descending)' },
  { id: 'chime_7', label: 'Chime 7 (Pluck)' },
  { id: 'chime_8', label: 'Chime 8 (Bell)' },
  { id: 'chime_9', label: 'Chime 9 (Short ping)' },
  { id: 'chime_10', label: 'Chime 10 (Alert)' },
]

export function getEmailSoundEnabled(): boolean {
  try {
    return localStorage.getItem(EMAIL_SOUND_ENABLED_KEY) === 'true'
  } catch {
    return false
  }
}

export function setEmailSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(EMAIL_SOUND_ENABLED_KEY, enabled ? 'true' : 'false')
  } catch {
    // ignore
  }
}

export function getEmailSoundId(): EmailSoundId {
  try {
    const raw = localStorage.getItem(EMAIL_SOUND_ID_KEY) as EmailSoundId | null
    const valid = EMAIL_SOUND_OPTIONS.some((o) => o.id === raw)
    return valid ? (raw as EmailSoundId) : 'chime_1'
  } catch {
    return 'chime_1'
  }
}

export function setEmailSoundId(id: EmailSoundId): void {
  try {
    localStorage.setItem(EMAIL_SOUND_ID_KEY, id)
  } catch {
    // ignore
  }
}

let audioCtx: AudioContext | null = null

function getAudioContext(): AudioContext | null {
  // Safari uses webkitAudioContext
  const AnyWindow = window as any
  const Ctx = window.AudioContext || AnyWindow.webkitAudioContext
  if (!Ctx) return null
  if (!audioCtx) audioCtx = new Ctx()
  return audioCtx
}

export async function primeEmailSound(): Promise<void> {
  const ctx = getAudioContext()
  if (!ctx) return
  if (ctx.state === 'suspended') {
    await ctx.resume()
  }
}

type SoundStep = {
  /** seconds offset from start */
  at: number
  /** seconds */
  dur: number
  /** Hz */
  freq: number
  type?: OscillatorType
  /** peak gain 0..1 */
  peak?: number
  /** exponential freq ramp target Hz (optional) */
  rampTo?: number
}

function playSteps(ctx: AudioContext, steps: SoundStep[], opts?: { masterPeak?: number }): void {
  const start = ctx.currentTime
  const masterPeak = Math.max(0.02, Math.min(0.25, opts?.masterPeak ?? 0.14))

  for (const s of steps) {
    const t0 = start + Math.max(0, s.at)
    const t1 = t0 + Math.max(0.02, s.dur)

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.type = s.type ?? 'sine'
    osc.frequency.setValueAtTime(Math.max(40, s.freq), t0)
    if (typeof s.rampTo === 'number') {
      osc.frequency.exponentialRampToValueAtTime(Math.max(40, s.rampTo), t0 + Math.min(0.18, s.dur * 0.7))
    }

    const peak = masterPeak * Math.max(0.15, Math.min(1, s.peak ?? 1))
    gain.gain.setValueAtTime(0.0001, t0)
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.015)
    gain.gain.exponentialRampToValueAtTime(0.0001, t1)

    osc.connect(gain)
    gain.connect(ctx.destination)

    try {
      osc.start(t0)
      osc.stop(t1 + 0.01)
    } catch {
      // ignore scheduling errors
    }
  }
}

function getStepsForSound(id: EmailSoundId): { steps: SoundStep[]; masterPeak?: number } {
  switch (id) {
    case 'chime_1':
      // classic two-tone
      return { steps: [{ at: 0, dur: 0.24, freq: 880, type: 'sine', rampTo: 660 }] }
    case 'chime_2':
      // soft triad
      return {
        masterPeak: 0.11,
        steps: [
          { at: 0.0, dur: 0.18, freq: 523.25, type: 'sine', peak: 0.9 },
          { at: 0.06, dur: 0.18, freq: 659.25, type: 'sine', peak: 0.85 },
          { at: 0.12, dur: 0.18, freq: 783.99, type: 'sine', peak: 0.8 },
        ],
      }
    case 'chime_3':
      // bright sparkle
      return {
        masterPeak: 0.13,
        steps: [
          { at: 0.0, dur: 0.12, freq: 1046.5, type: 'triangle', peak: 0.95 },
          { at: 0.07, dur: 0.14, freq: 1318.5, type: 'triangle', peak: 0.8 },
        ],
      }
    case 'chime_4':
      // double ding
      return {
        steps: [
          { at: 0.0, dur: 0.14, freq: 784, type: 'sine', peak: 1 },
          { at: 0.18, dur: 0.14, freq: 784, type: 'sine', peak: 0.95 },
        ],
      }
    case 'chime_5':
      // ascending
      return {
        steps: [
          { at: 0.0, dur: 0.12, freq: 660, type: 'sine', peak: 0.9 },
          { at: 0.10, dur: 0.12, freq: 880, type: 'sine', peak: 0.95 },
          { at: 0.20, dur: 0.14, freq: 1175, type: 'sine', peak: 1.0 },
        ],
      }
    case 'chime_6':
      // descending
      return {
        steps: [
          { at: 0.0, dur: 0.14, freq: 1175, type: 'sine', peak: 1.0 },
          { at: 0.12, dur: 0.12, freq: 880, type: 'sine', peak: 0.95 },
          { at: 0.22, dur: 0.12, freq: 660, type: 'sine', peak: 0.9 },
        ],
      }
    case 'chime_7':
      // plucky (square-ish)
      return {
        masterPeak: 0.10,
        steps: [
          { at: 0.0, dur: 0.08, freq: 740, type: 'square', peak: 1.0, rampTo: 620 },
          { at: 0.10, dur: 0.08, freq: 740, type: 'square', peak: 0.8, rampTo: 620 },
        ],
      }
    case 'chime_8':
      // bell-like (two partials)
      return {
        masterPeak: 0.12,
        steps: [
          { at: 0.0, dur: 0.26, freq: 988, type: 'sine', peak: 0.9 },
          { at: 0.0, dur: 0.22, freq: 1482, type: 'triangle', peak: 0.55 },
        ],
      }
    case 'chime_9':
      // short ping
      return { masterPeak: 0.12, steps: [{ at: 0.0, dur: 0.10, freq: 1046.5, type: 'sine', peak: 1.0 }] }
    case 'chime_10':
      // alert (quick triple)
      return {
        masterPeak: 0.14,
        steps: [
          { at: 0.0, dur: 0.09, freq: 988, type: 'triangle', peak: 1.0 },
          { at: 0.11, dur: 0.09, freq: 988, type: 'triangle', peak: 0.95 },
          { at: 0.22, dur: 0.10, freq: 1175, type: 'triangle', peak: 1.0 },
        ],
      }
    default:
      return { steps: [{ at: 0, dur: 0.24, freq: 880, type: 'sine', rampTo: 660 }] }
  }
}

async function playEmailSound(opts?: { soundId?: EmailSoundId; force?: boolean }): Promise<void> {
  if (opts?.force !== true && !getEmailSoundEnabled()) return

  const ctx = getAudioContext()
  if (!ctx) return
  try {
    if (ctx.state === 'suspended') await ctx.resume()
  } catch {
    // browser may block without user gesture
    return
  }

  const id = opts?.soundId ?? getEmailSoundId()
  const { steps, masterPeak } = getStepsForSound(id)
  playSteps(ctx, steps, { masterPeak })
}

export async function playNewEmailSound(): Promise<void> {
  await playEmailSound()
}






